import React, { useState } from "react";
import { Alert, Box, Button, CircularProgress, Divider, Grid, Switch, Typography, useTheme } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { DataLayout, DonutChart, DrawerControl, FilterButton, FilterSideBar, FieldControl } from "../../../shared/components";
import { useFilterContext } from "../../../shared/context";
import { useActiveFilterLabels } from "../../../shared/hooks";
import { useDisplayField } from "../../../hooks/useDisplayField";

import { useCriticalRisks, useRiskCategories, useRiskMatrix, useRiskSummary } from "../mutation";

import { RiskCard } from "./RiskCard";
import { RiskHeatmap } from "./RiskHeatmap";
import { RiskDetailsCard } from "./RiskDetailsCard";

const HIDDEN_FILTERS = ["stakeholder", "project_status"] as const;

export const RiskOverView: React.FC<{ projectId: string | undefined }> = ({ projectId }) => {

    const theme = useTheme();

    const { quickFilters, handleResetFilters } = useFilterContext();
    const { getFilterOptions } = useDisplayField([]);
    const { hasActiveFilters: hasActiveQuickFilters } = useActiveFilterLabels([...HIDDEN_FILTERS]);

    const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
    const handleOpenFilter = () => setIsOpenFilter(true);
    const handleCloseFilter = () => setIsOpenFilter(false);
    const [assignedToMe, setAssignedToMe] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
    const [statusDraft, setStatusDraft] = useState<string | null>(null);
    const [categoryDraft, setCategoryDraft] = useState<string | null>(null);

    const handleAssignedToMeChange = () => {
        setAssignedToMe(!assignedToMe);
    };

    const handleStatusFilterChange = (status: string) => {
        setStatusDraft(status || null);
    };

    const handleCategoryFilterChange = (category: string) => {
        setCategoryDraft(category || null);
    };

    const handleApplyExtraFilters = () => {
        setStatusFilter(statusDraft);
        setCategoryFilter(categoryDraft);
    };

    const handleResetExtraFilters = () => {
        setStatusDraft(null);
        setCategoryDraft(null);
        setStatusFilter(null);
        setCategoryFilter(null);
    };

    const hasActiveFilters = hasActiveQuickFilters || assignedToMe || !!statusFilter || !!categoryFilter;

    const handleResetAllFilters = () => {
        setAssignedToMe(false);
        setStatusFilter(null);
        setCategoryFilter(null);
        setStatusDraft(null);
        setCategoryDraft(null);
        handleResetFilters();
    };

    const riskFilters = {
        projectId,
        assignedToMe,
        quickFilters,
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
    };

    const { isPending: isCriticalRisksPending, criticalRiskData, isError: isCriticalRisksError, error: criticalRisksError } = useCriticalRisks(riskFilters);
    const { isPending: isRiskCategoriesPending, riskCategoryData, isError: isRiskCategoriesError, error: riskCategoriesError } = useRiskCategories(riskFilters);
    const { isPending: isRiskMatrixPending, riskMatrixData, isError: isRiskMatrixError, error: riskMatrixError } = useRiskMatrix(riskFilters);
    const { isPending: isRiskSummaryPending, riskSummaryData, isError: isRiskSummaryError, error: riskSummaryError } = useRiskSummary(projectId);

    const colorPalette = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.info.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
    ];

    const getRandomColor = (index: number) => {
        return colorPalette[index % colorPalette.length];
    };

    const chartData = riskCategoryData.map((item: { category_percentage: string; category: string; }, index: any) => ({
        id: index,
        label: item.category,
        value: parseInt(item.category_percentage, 10) || 0,
        color: getRandomColor(index),
        legendLabel: item.category?.concat(` ${item.category_percentage}%`),
        legendValue: `${item.category_percentage}%`
    }));

    const riskExtraFilters = (
        <Box display="flex" flexDirection="column" gap={2}>
            <FieldControl
                keyName="riskStatus"
                label="Status"
                variant="select"
                value={statusDraft ?? ''}
                options={getFilterOptions('RISK_OVERVIEW_STATUS').map((domain: any) => ({
                    value: domain.key,
                    label: domain.display_text,
                }))}
                onChange={(_key, value) => handleStatusFilterChange(value)}
            />
            <FieldControl
                keyName="riskCategory"
                label="Category"
                variant="select"
                value={categoryDraft ?? ''}
                options={getFilterOptions('RISK_CATEGORY').map((domain: any) => ({
                    value: domain.key,
                    label: domain.display_text,
                }))}
                onChange={(_key, value) => handleCategoryFilterChange(value)}
            />
        </Box>
    );

    if (isRiskSummaryPending || isRiskCategoriesPending || isCriticalRisksPending || isRiskMatrixPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    if (isCriticalRisksError || isRiskCategoriesError || isRiskMatrixError || isRiskSummaryError) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
            >
                <Alert
                    severity="warning"
                    sx={{
                        width: "auto",
                        justifyContent: "center",
                        textAlign: "center",
                    }}
                >
                    {criticalRiskData?.message || riskCategoryData?.message || riskMatrixData?.message || riskSummaryData?.message || "Something went wrong!"}
                </Alert>
            </Box>
        );
    };


    return (
        <Box p={2} display="flex" flexDirection="column" gap={2}>
            <Box display={'flex'} alignItems={'center'} gap={2}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 'auto' }}>
                    <Button
                        startIcon={<RestartAltIcon />}
                        onClick={handleResetAllFilters}
                        disabled={!hasActiveFilters}
                    >
                        RESET FILTERS
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="subtitle2">Raised by Me :</Typography>
                        <Switch checked={assignedToMe} onChange={handleAssignedToMeChange} />
                    </Box>
                    <FilterButton hiddenFilters={[...HIDDEN_FILTERS]} onClick={handleOpenFilter} />
                </Box>
            </Box>
            <Divider />
            <Grid container spacing={2}>
                <Grid size={4}>
                    <RiskCard title={'Total Risks'} value={riskSummaryData?.total_risk || 0} status={'error'} />
                </Grid>
                <Grid size={4}>
                    <RiskCard title={'Open Risks'} value={riskSummaryData?.open_risk_count || 0} status={'warning'} />
                </Grid>
                <Grid size={4}>
                    <RiskCard title={'Resolution Rate'} value={`${riskSummaryData?.resolution_rate || 0}%`} status={'success'} />
                </Grid>
            </Grid>
            <Box display={'flex'} alignItems={'stretch'} gap={2}>
                <DataLayout title="Risk Categories">
                    <Box
                        display="flex"
                        gap={2}
                        flexDirection="column"
                        p={2}
                        sx={{
                            height: 250,
                            overflowY: 'auto'
                        }}
                    >
                        {
                            riskCategoryData.length === 0 ? (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    alignContent="center"
                                    width="100%"
                                    height="100%"
                                    gap={2}
                                    mt={5}
                                >
                                    <Typography variant="body2">No risk categories data available.</Typography>
                                </Box>
                            ) :
                                <Box height={250}>
                                    <DonutChart data={chartData} />
                                </Box>
                        }
                    </Box>
                </DataLayout>
                <DataLayout title="Critical Risks">
                    <Box
                        display="flex"
                        gap={2}
                        flexDirection="column"
                        p={2}
                        sx={{
                            height: 250,
                            overflowY: 'auto'
                        }}
                    >
                        {
                            criticalRiskData.length === 0 ? (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    alignContent="center"
                                    width="100%"
                                    height="100%"
                                    gap={2}
                                    mt={5}
                                >
                                    <Typography variant="body2">No critical risks found.</Typography>
                                </Box>
                            ) :
                                criticalRiskData.map((risk: {
                                    project_ref: string,
                                    title: string,
                                    category: string,
                                    risk_rag: string
                                }, index: React.Key) => (
                                    <RiskDetailsCard risk={risk} key={index} />
                                ))
                        }
                    </Box>
                </DataLayout>
            </Box>
            <DataLayout title="Risk Matrix">
                <RiskHeatmap riskMatrixDetails={riskMatrixData} />
            </DataLayout>

            <DrawerControl
                title="Filter Records"
                subTitle="Please apply following filters to refine results"
                isOpen={isOpenFilter}
                handleClose={handleCloseFilter}
            >
                <FilterSideBar
                    hiddenFilters={[...HIDDEN_FILTERS]}
                    extraFilters={riskExtraFilters}
                    onApplyExtra={handleApplyExtraFilters}
                    onResetExtra={handleResetExtraFilters}
                    onClose={handleCloseFilter}
                />
            </DrawerControl>
        </Box>
    )

};